import { buildBreadcrumbLabel, type LocaleLabelKey } from '@/constant/locale-labels'

export interface BreadcrumbItem {
  label: string
  href?: string
  isCurrentPage?: boolean
}

// Function to resolve dynamic breadcrumb configuration
// This function handles routes with dynamic parameters like :id, :slug, etc.
//
// Usage examples:
// - /document-repo/123/folder -> Document Repository > Repository 123 > Folder
// - /admin/organisations/456 -> Organisations > Organisation 456
// - /admin/clerk-users/789 -> Clerk Users > User 789
//
// To add new dynamic routes:
// 1. Add a new entry to the dynamicRoutes object
// 2. Define the pattern with :paramName
// 3. Create a resolver function that returns BreadcrumbItem[]
export const resolveBreadcrumbConfig = (
  pathname: string,
  t?: (key: LocaleLabelKey) => string,
  search?: string
): BreadcrumbItem[] => {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/'
  const searchParams = new URLSearchParams(search ?? '')

  // Handle dynamic routes with parameters
  const dynamicRoutes: Record<string, (params: Record<string, string>) => BreadcrumbItem[]> = {
    '/document-repo/:id/folder': () => [
      { label: 'Document Repository', href: '/document-repo' },
      { label: 'Folder', isCurrentPage: true },
    ],
    '/document-repo/:id/folder/:folderId/sub-folder': (params) => [
      { label: 'Document Repository', href: '/document-repo' },
      { label: 'Folder', href: `/document-repo/${params.id}/folder` },
      { label: 'Sub Folder', isCurrentPage: true },
    ],
    '/on-boarding/:id': () => [{ label: 'Organisation Onboarding', isCurrentPage: true }],
    '/add-risk/:id': () => [
      { label: 'Risk Dashboard', href: '/risk-dashboard' },
      { label: 'Manage Organisation Risk', href: '/risk-list' },
      {
        label: searchParams.get('edit') === 'true' ? 'Update Risk' : 'Add Risk',
        isCurrentPage: true,
      },
    ],
    '/add-incident/:id': () => [
      { label: 'Incident Dashboard', href: '/incident-dashboard' },
      { label: 'Incident List', href: '/incident' },
      {
        label: searchParams.get('edit') === 'true' ? 'Update Incident' : 'Add Incident',
        isCurrentPage: true,
      },
    ],
    '/add-service-provider/:id': () => [
      { label: 'Supply Chain Register', href: '/service-provider-list' },
      {
        label: searchParams.get('edit') === 'true' ? 'Update Supplier' : 'Add Supplier',
        isCurrentPage: true,
      },
    ],
    '/add-information-security-objective/:id': () => [
      { label: 'Information Security Objectives', href: '/information-security-objectives' },
      {
        label:
          searchParams.get('edit') === 'true'
            ? 'Update Information Security Objective'
            : 'Add Information Security Objective',
        isCurrentPage: true,
      },
    ],
    '/archive-assessment/:organisationStandardId': () => [
      { label: 'Archive Assessment', href: '/archive-assessment' },
      { label: 'Archive Assessment', isCurrentPage: true },
    ],
    '/archive-assessment/pci-assessment/:organisationStandardId': () => [
      { label: 'Archive Assessment', href: '/archive-assessment' },
      { label: 'PCI Assessment', isCurrentPage: true },
    ],
    '/archive-assessment/pci-saq-assessment/:organisationStandardId': () => [
      { label: 'Archive Assessment', href: '/archive-assessment' },
      { label: 'PCI SAQ Assessment', isCurrentPage: true },
    ],
    '/archive-assessment/ism-irap-assessment/:organisationStandardId': () => [
      { label: 'Archive Assessment', href: '/archive-assessment' },
      { label: 'ISM IRAP Assessment', isCurrentPage: true },
    ],
    '/archive-assessment/csp-assessment/:organisationStandardId': () => [
      { label: 'Archive Assessment', href: '/archive-assessment' },
      { label: 'CSP Assessment', isCurrentPage: true },
    ],
    '/assessments/start-assessments/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'Assessment Configuration', isCurrentPage: true },
    ],
    '/assessments/start-pci-assessment/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'Start PCI Assessment', isCurrentPage: true },
    ],
    '/assessments/start-pci-roc-assessment/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'PCI ROC Configuration', isCurrentPage: true },
    ],
    '/assessments/start-pci-saq-assessment/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'PCI SAQ Configuration', isCurrentPage: true },
    ],
    '/assessments/pci-saq-assessment/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'PCI SAQ Assessment', isCurrentPage: true },
    ],
    '/assessments/pci-scoping-configuration/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'PCI Scoping Configuration', isCurrentPage: true },
    ],
    '/assessments/pci-scoping-configuration/:organisationStandardId/:scopingId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'PCI Scoping Configuration', isCurrentPage: true },
    ],
    '/assessments/ism-scoping-configuration/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'ISM Scoping Configuration', isCurrentPage: true },
    ],
    '/assessments/ism-scoping-configuration/:organisationStandardId/:scopingId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'ISM Scoping Configuration', isCurrentPage: true },
    ],
    '/assessments/start-ism-irap-assessment/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'ISM IRAP Configuration', isCurrentPage: true },
    ],
    '/assessments/ism-irap-assessment/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'ISM IRAP Assessment', isCurrentPage: true },
    ],
    '/assessments/ism-principle-assessment/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'ISM Principle Assessment', isCurrentPage: true },
    ],
    '/assessments/start-ism-gap-assessment/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'ISM GAP Configuration', isCurrentPage: true },
    ],
    '/assessments/start-cis-assessment/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'CIS GAP Configuration', isCurrentPage: true },
    ],
    '/assessments/start-soc2-assessment/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'SOC 2 GAP Configuration', isCurrentPage: true },
    ],
    '/assessments/start-csp-assessment/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'CSP Configuration', isCurrentPage: true },
    ],
    '/assessments/csp-assessment/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'CSP Assessment', isCurrentPage: true },
    ],
    '/assessments/csp-assessment/review/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'CSP Assessment Review', isCurrentPage: true },
    ],
    '/assessments/csp-assessment/review/:organisationStandardId/controls': (params) => [
      { label: 'Assessment', href: '/assessments' },
      {
        label: 'CSP Assessment Review',
        href: `/assessments/csp-assessment/review/${params.organisationStandardId}`,
      },
      { label: 'Review Controls', isCurrentPage: true },
    ],
    '/assessments/csp-assessment/attestation/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'CSP Assessment Attestation and Sign-off', isCurrentPage: true },
    ],
    '/assessments/csp-assessment/exception-request/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'CSP Exception Request', isCurrentPage: true },
    ],
    '/assessments/:organisationStandardId': () => [
      { label: 'Assessments', href: '/assessments' },
      { label: 'Assessment Detail', isCurrentPage: true },
    ],
    '/assessments/pci-assessment/:organisationStandardId': () => [
      { label: 'Assessments', href: '/assessments' },
      { label: 'Assessment Detail', isCurrentPage: true },
    ],
    '/ai-assessment/:organisationStandardId': () => [
      { label: 'Assessments', href: '/assessments' },
      { label: 'AI Assessment', isCurrentPage: true },
    ],
    '/audit/:organisationStandardId': () => [
      { label: 'Assessments', href: '/assessments' },
      { label: 'Audit', isCurrentPage: true },
    ],
    '/non-conformity/:organisationStandardId': () => [
      { label: 'Assessments', href: '/assessments' },
      { label: 'Non-Conformity', isCurrentPage: true },
    ],
    '/assessments/logs/:organisationStandardId': () => [
      { label: 'Assessments', href: '/assessments' },
      { label: 'Logs', isCurrentPage: true },
    ],
    '/reporting/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'Report', isCurrentPage: true },
    ],
    '/audit-list/:organisationStandardId': () => [
      { label: 'Assessments', href: '/assessments' },
      { label: 'Audit List', isCurrentPage: true },
    ],
    '/assessment-library/questionnaire-builder/:id': () => [
      { label: 'Questionnaire Library', href: '/assessment-library' },
      { label: 'Questionnaire Builder', isCurrentPage: true },
    ],
    '/add-service-provider-assessment/:id': () => [
      { label: 'Supply Chain Assessment', href: '/service-provider-assessment' },
      { label: 'Supply Chain Assessment', isCurrentPage: true },
    ],
    '/service-provider-assessment/:assessmentId/evaluation': () => [
      { label: 'Supply Chain Register', href: '/service-provider-list' },
      { label: 'Supply Chain Assessment', href: '/service-provider-assessment' },
      { label: 'Vendor Evaluation', isCurrentPage: true },
    ],
    '/service-provider-incoming-questionnaire/:id': () => [
      { label: 'Incoming Questionnaire', href: '/service-provider-incoming-questionnaire' },
      { label: 'Incoming Questionnaire Details', isCurrentPage: true },
    ],
    '/add-assessment-library/:id': () => [
      { label: 'Questionnaire Library', href: '/assessment-library' },
      {
        label: searchParams.get('edit') === 'true' ? 'Update Questionnaire Library' : 'Add Questionnaire Library',
        isCurrentPage: true,
      },
    ],
    '/admin/risk-assessment/questionnaire-library/:questionnaireId': () => [
      { label: 'System Risk Assessment Setup', href: '#' },
      { label: 'Questionnaire Library', href: '/admin/risk-assessment/questionnaire-library' },
      { label: 'Questionnaire Builder', isCurrentPage: true },
    ],
    '/admin/people-risk-assessment/questionnaire-library/:questionnaireId': () => [
      { label: 'People Risk Assessment Setup', href: '#' },
      { label: 'Questionnaire Library', href: '/admin/people-risk-assessment/questionnaire-library' },
      { label: 'Questionnaire Builder', isCurrentPage: true },
    ],
    '/admin/organisations/child-organisations/:id': () => [
      { label: 'Organisations', href: '/admin/organisations' },
      { label: 'Child Organisations', isCurrentPage: true },
    ],

    '/assessments/saq-report/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'SAQ Report', isCurrentPage: true },
    ],
    '/vendor/assessments/:id': () => [
      { label: 'Vendor Assessments', href: '/vendor/assessments' },
      { label: 'Assessment Detail', isCurrentPage: true },
    ],
    '/service-provider/:id/impact-assessment': () => [
      { label: 'Supply Chain Register', href: '/service-provider-list' },
      { label: 'Impact Assessment', isCurrentPage: true },
    ],
    '/service-provider/:id/risk-posture': () => [
      { label: 'Supply Chain Register', href: '/service-provider-list' },
      { label: 'Risk Posture', isCurrentPage: true },
    ],
    '/service-provider-incoming-questionnaire/:id/answer': () => [
      { label: 'Incoming Questionnaire', href: '/service-provider-incoming-questionnaire' },
      { label: 'Answer Questionnaire', isCurrentPage: true },
    ],
    '/service-provider-report-library': () => [
      { label: 'Supply Chain Management', href: '/service-provider-list' },
      { label: 'Report Library', isCurrentPage: true },
    ],
    '/service-provider-ai-risk/:id/edit': () => [
      { label: 'Supply Chain Management', href: '/service-provider-list' },
      { label: 'Edit Risk', isCurrentPage: true },
    ],
    '/ctra/engagements/add/:id': () => [
      { label: 'Cyber Threat Risk Assessment', isCurrentPage: true },
      { label: 'Engagements', href: '/ctra/engagements' },
      {
        label: searchParams.get('edit') === 'true' ? 'Update Engagement' : 'Add Engagement',
        isCurrentPage: true,
      },
    ],
    '/ctra/engagements/add': () => [
      { label: 'Cyber Threat Risk Assessment', isCurrentPage: true },
      { label: 'Engagements', href: '/ctra/engagements' },
      { label: 'Add Engagement', isCurrentPage: true },
    ],
    '/assessments/csp-assessment/reporting-calendar/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'CSP Reporting Calendar', isCurrentPage: true },
    ],
    '/assessments/csp-assessment/dashboard/:organisationStandardId': () => [
      { label: 'Assessment', href: '/assessments' },
      { label: 'CSP Dashboard', isCurrentPage: true },
    ],
    '/ctra/engagements/:id': () => [
      { label: 'Cyber Threat Risk Assessment', isCurrentPage: true },
      { label: 'Engagements', href: '/ctra/engagements' },
      { label: 'Engagement Overview', isCurrentPage: true },
    ],
    '/ctra/engagements/:id/promote-risks': () => [
      { label: 'Cyber Threat Risk Assessment', isCurrentPage: true },
      { label: 'Engagements', href: '/ctra/engagements' },
      { label: 'Promote Risks', isCurrentPage: true },
    ],
    '/ctra/engagements/:id/add-risk': () => [
      { label: 'Cyber Threat Risk Assessment', isCurrentPage: true },
      { label: 'Engagements', href: '/ctra/engagements' },
      { label: 'Add Risk', isCurrentPage: true },
    ],
    '/ctra/engagements/:id/add-risk/:riskId': () => [
      { label: 'Cyber Threat Risk Assessment', isCurrentPage: true },
      { label: 'Engagements', href: '/ctra/engagements' },
      { label: 'Update Risk', isCurrentPage: true },
    ],
    '/ctra/engagements/:id/questionnaire-batch': () => [
      { label: 'Cyber Threat Risk Assessment', isCurrentPage: true },
      { label: 'Engagements', href: '/ctra/engagements' },
      { label: 'Questionnaire Batch', isCurrentPage: true },
    ],
    '/ctra/engagements/:id/questionnaire-batch/:batchId': () => [
      { label: 'Cyber Threat Risk Assessment', isCurrentPage: true },
      { label: 'Engagements', href: '/ctra/engagements' },
      { label: 'Questionnaire Batch', isCurrentPage: true },
    ],
    '/admin/user-guides/:id': () => [
      { label: 'User Guides', href: '/admin/user-guides' },
      { label: 'User Guide Detail', isCurrentPage: true },
    ],
  }

  // Check if the pathname matches any dynamic route pattern
  for (const [pattern, resolver] of Object.entries(dynamicRoutes)) {
    const regexPattern = pattern.replace(/:[^/]+/g, '([^/]+)')
    const regex = new RegExp(`^${regexPattern}$`)
    const match = normalizedPath.match(regex)

    if (match) {
      // Extract parameter names from the pattern
      const paramNames = (pattern.match(/:[^/]+/g) || []).map((param) => param.slice(1))
      // Create params object
      const params: Record<string, string> = {}
      paramNames.forEach((paramName, index) => {
        params[paramName] = match[index + 1]
      })

      const dynamicItems = resolver(params)
      return resolveLabels(dynamicItems, t)
    }
  }

  // Return static configuration if no dynamic route matches
  const rawItems = breadcrumbConfig[normalizedPath] || []
  return resolveLabels(rawItems, t)
}

function resolveLabels(items: BreadcrumbItem[], t?: (key: LocaleLabelKey) => string): BreadcrumbItem[] {
  if (!t) return items
  return items.map((item) => {
    const built = buildBreadcrumbLabel(item.label, t)
    return built !== item.label ? { ...item, label: built } : item
  })
}

// Breadcrumb configuration for all routes
export const breadcrumbConfig: Record<string, BreadcrumbItem[]> = {
  '/': [{ label: '', isCurrentPage: true }],
  '/manage-users': [{ label: 'Manage Users', isCurrentPage: true }],
  '/manage-projects': [
    { label: 'Task Dashboard', href: '/tasks-dashboard' },
    { label: 'Manage Projects', isCurrentPage: true },
  ],
  '/manage-projects/tasks': [
    { label: 'Task Dashboard', href: '/tasks-dashboard' },
    { label: 'Manage Projects', href: '/manage-projects' },
    { label: 'Tasks', isCurrentPage: true },
  ],
  '/tasks-dashboard': [{ label: 'Task Dashboard', isCurrentPage: true }],
  '/login-logs': [{ label: 'Login Logs', isCurrentPage: true }],
  '/activity-logs': [{ label: 'Activity Logs', isCurrentPage: true }],
  '/activity-logs/all-users': [{ label: 'Show All User Activity Logs', isCurrentPage: true }],
  '/activity-analytics': [{ label: 'Activity Analytics', isCurrentPage: true }],
  '/notifications': [{ label: 'Notifications', isCurrentPage: true }],
  '/email-logs': [{ label: 'Email Logs', isCurrentPage: true }],
  '/manage-organisation': [{ label: 'Manage Organisation', isCurrentPage: true }],
  '/policy': [
    { label: 'Policy Dashboard', href: '/policy-dashboard' },
    { label: 'Policy', isCurrentPage: true },
  ],
  '/policy-dashboard': [{ label: 'Policy Dashboard', isCurrentPage: true }],
  '/meeting-minutes': [{ label: 'Meeting Minutes', isCurrentPage: true }],
  '/meeting-minutes-type': [
    { label: 'Meeting Minutes', href: '/meeting-minutes' },
    { label: 'Meeting Minutes Type', isCurrentPage: true },
  ],
  '/compliance-report': [{ label: 'Compliance Dashboard', isCurrentPage: true }],
  '/report-library': [
    { label: 'Compliance Dashboard', href: '/compliance-report' },
    { label: 'Report Library', isCurrentPage: true },
  ],
  '/issues/non-conformities': [
    { label: 'Compliance Dashboard', href: '/compliance-report' },
    { label: 'Issues', href: '/issues/non-conformities' },
    { label: 'Non-Conformities', isCurrentPage: true },
  ],
  '/issues/opportunities-for-improvement': [
    { label: 'Compliance Dashboard', href: '/compliance-report' },
    { label: 'Issues', href: '/issues/non-conformities' },
    { label: 'Opportunities for Improvement', isCurrentPage: true },
  ],
  '/statement-of-applicability': [
    { label: 'Compliance Dashboard', href: '/compliance-report' },
    { label: 'Statement Of Applicability', isCurrentPage: true },
  ],
  '/quarantine-management': [
    { label: 'Compliance Dashboard', href: '/compliance-report' },
    { label: 'Quarantine Management', isCurrentPage: true },
  ],
  '/settings': [{ label: 'Settings', isCurrentPage: true }],
  '/settings/security/password-policy': [
    { label: 'Settings', href: '/settings' },
    { label: 'Password Policy', isCurrentPage: true },
  ],
  '/settings/security/sso': [
    { label: 'Settings', href: '/settings' },
    { label: 'SSO', isCurrentPage: true },
  ],
  '/settings/security/signature-settings': [
    { label: 'Settings', href: '/settings' },
    { label: 'Signature Settings', isCurrentPage: true },
  ],
  '/settings/utility': [{ label: 'Utility Settings', isCurrentPage: true }],
  '/settings/field-requirements': [{ label: 'Required Fields', isCurrentPage: true }],
  '/settings/utility/toaster': [
    { label: 'Utility Settings', href: '/settings/utility' },
    { label: 'Toaster Settings', isCurrentPage: true },
  ],
  '/settings/credential-registry': [{ label: 'Credential Registry', isCurrentPage: true }],
  '/settings/credential-type': [
    { label: 'QSA/ISA', isCurrentPage: true },
    { label: 'Credential Type', isCurrentPage: true },
  ],
  '/settings/external-integration': [{ label: 'API Integrations', isCurrentPage: true }],
  '/settings/vanta-integration': [
    { label: 'API Integrations', href: '/settings/external-integration' },
    { label: 'Vanta Integration', isCurrentPage: true },
  ],
  '/settings/drata-integration': [
    { label: 'API Integrations', href: '/settings/external-integration' },
    { label: 'Drata Integration', isCurrentPage: true },
  ],
  '/settings/api-tokens': [{ label: 'API Tokens', isCurrentPage: true }],
  '/security-awareness/phished': [
    { label: 'Security Awareness', isCurrentPage: true },
    { label: 'Phished', isCurrentPage: true },
  ],
  '/security-awareness/mimecast': [
    { label: 'Security Awareness', isCurrentPage: true },
    { label: 'Mimecast', isCurrentPage: true },
  ],
  '/settings/policy/policy-category': [
    { label: 'Policy Dashboard', href: '/policy-dashboard' },
    { label: 'Policy', href: '/policy' },
    { label: 'Policy Category', isCurrentPage: true },
  ],
  '/settings/policy/default-policy-reviewer': [
    { label: 'Policy Dashboard', href: '/policy-dashboard' },
    { label: 'Policy', href: '/policy' },
    { label: 'Default Policy Reviewer', isCurrentPage: true },
  ],
  '/settings/jira-integration': [
    { label: 'API Integrations', href: '/settings/external-integration' },
    { label: 'Jira Integration', isCurrentPage: true },
  ],
  '/settings/azure-integration': [
    { label: 'API Integrations', href: '/settings/external-integration' },
    { label: 'Azure Integration', isCurrentPage: true },
  ],
  '/settings/google-drive-integration': [
    { label: 'API Integrations', href: '/settings/external-integration' },
    { label: 'Google Drive Integration', isCurrentPage: true },
  ],
  '/settings/email-configuration': [
    { label: 'API Integrations', href: '/settings/external-integration' },
    { label: 'Email Configuration', isCurrentPage: true },
  ],
  '/settings/google-drive-integration/callback': [
    { label: 'API Integrations', href: '/settings/external-integration' },
    { label: 'Google Drive Integration', isCurrentPage: true },
  ],
  '/settings/confluence-integration': [
    { label: 'API Integrations', href: '/settings/external-integration' },
    { label: 'Confluence Integration', isCurrentPage: true },
  ],
  '/settings/palo-alto-networks-integration': [
    { label: 'API Integrations', href: '/settings/external-integration' },
    { label: 'Palo Alto Networks Integration', isCurrentPage: true },
  ],
  '/settings/ms-defender-integration': [
    { label: 'API Integrations', href: '/settings/external-integration' },
    { label: 'MS Defender Integration', isCurrentPage: true },
  ],
  '/settings/mimecast-integration': [
    { label: 'API Integrations', href: '/settings/external-integration' },
    { label: 'Mimecast Integration', isCurrentPage: true },
  ],
  '/settings/zapier-webhook': [
    { label: 'API Integrations', href: '/settings/external-integration' },
    { label: 'Zapier Webhook', isCurrentPage: true },
  ],
  '/settings/n8n-webhook': [
    { label: 'API Integrations', href: '/settings/external-integration' },
    { label: 'N8N Webhook', isCurrentPage: true },
  ],
  '/settings/policy/policy-frequencies': [
    { label: 'Policy Dashboard', href: '/policy-dashboard' },
    { label: 'Policy', href: '/policy' },
    { label: 'Policy Update Frequencies', isCurrentPage: true },
  ],
  '/settings/policy/dashboard-benchmarks': [
    { label: 'Policy Dashboard', href: '/policy-dashboard' },
    { label: 'Policy', href: '/policy' },
    { label: 'Policy Dashboard Benchmarks', isCurrentPage: true },
  ],
  '/settings/task/task-status': [
    { label: 'Task', isCurrentPage: true },
    { label: 'Task Status', isCurrentPage: true },
  ],
  '/settings/task/task-severity': [
    { label: 'Task', isCurrentPage: true },
    { label: 'Task Severity', isCurrentPage: true },
  ],
  '/settings/service-provider/service-provider-contract-status': [
    { label: 'Supply Chain Register', href: '/service-provider-list' },
    { label: 'Supply Chain Contract Status', isCurrentPage: true },
  ],
  '/settings/service-provider/service-type': [
    { label: 'Supply Chain Register', href: '/service-provider-list' },
    { label: 'Service Type', isCurrentPage: true },
  ],
  '/settings/service-provider/assessment-tier': [
    { label: 'Supply Chain Register', href: '/service-provider-list' },
    { label: 'Assessment Tier', isCurrentPage: true },
  ],
  '/settings/service-provider/assessment-tier-criticality': [
    { label: 'Supply Chain Register', href: '/service-provider-list' },
    { label: 'Supply Chain Criticality', isCurrentPage: true },
  ],
  '/settings/service-provider/assessment-tier-sensitivity': [
    { label: 'Supply Chain Register', href: '/service-provider-list' },
    { label: 'Supply Chain Sensitivity', isCurrentPage: true },
  ],
  '/settings/service-provider/control-mapping': [
    { label: 'Settings', href: '/settings' },
    { label: 'Control Mapping', isCurrentPage: true },
  ],
  '/settings/service-provider/scoring-rubric': [
    { label: 'Settings', href: '/settings' },
    { label: 'Scoring Rubric', isCurrentPage: true },
  ],
  '/settings/service-provider/risk-tier-matrix': [
    { label: 'Settings', href: '/settings' },
    { label: 'Risk Tier Matrix', isCurrentPage: true },
  ],
  '/settings/service-provider/risk-review-intervals': [
    { label: 'Supply Chain Register', href: '/service-provider-list' },
    { label: 'Risk Review Intervals', isCurrentPage: true },
  ],
  '/settings/risk/risk-likelihood': [
    { label: 'Risk Dashboard', href: '/risk-dashboard' },
    { label: 'Manage Organisation Risk', href: '/risk-list' },
    { label: 'Risk Likelihood', isCurrentPage: true },
  ],
  '/settings/risk/risk-impact': [
    { label: 'Risk Dashboard', href: '/risk-dashboard' },
    { label: 'Manage Organisation Risk', href: '/risk-list' },
    { label: 'Risk Impact', isCurrentPage: true },
  ],
  '/settings/risk/risk-rating': [
    { label: 'Risk Dashboard', href: '/risk-dashboard' },
    { label: 'Manage Organisation Risk', href: '/risk-list' },
    { label: 'Risk Rating', isCurrentPage: true },
  ],
  '/settings/risk/risk-matrix': [
    { label: 'Risk Dashboard', href: '/risk-dashboard' },
    { label: 'Manage Organisation Risk', href: '/risk-list' },
    { label: 'Risk Matrix', isCurrentPage: true },
  ],
  '/settings/risk/risk-categories': [
    { label: 'Risk Dashboard', href: '/risk-dashboard' },
    { label: 'Manage Organisation Risk', href: '/risk-list' },
    { label: 'Risk Categories', isCurrentPage: true },
  ],
  '/settings/risk/treatment': [
    { label: 'Risk Dashboard', href: '/risk-dashboard' },
    { label: 'Manage Organisation Risk', href: '/risk-list' },
    { label: 'Treatment', isCurrentPage: true },
  ],
  '/settings/pdf-template': [{ label: 'PDF Templates', isCurrentPage: true }],
  '/settings/risk/risk-owners': [
    { label: 'Risk Dashboard', href: '/risk-dashboard' },
    { label: 'Manage Organisation Risk', href: '/risk-list' },
    { label: 'Risk Owners', isCurrentPage: true },
  ],
  '/settings/risk/risk-identification-source': [
    { label: 'Risk Dashboard', href: '/risk-dashboard' },
    { label: 'Manage Organisation Risk', href: '/risk-list' },
    { label: 'Risk Identification Source', isCurrentPage: true },
  ],
  '/settings/non-conformity/root-identification': [
    { label: 'Non-Conformity', isCurrentPage: true },
    { label: 'Root Identification', isCurrentPage: true },
  ],
  '/settings/non-conformity/affected-area': [
    { label: 'Non-Conformity', isCurrentPage: true },
    { label: 'Affected Area', isCurrentPage: true },
  ],
  '/settings/non-conformity/importance': [
    { label: 'Non-Conformity', isCurrentPage: true },
    { label: 'Importance', isCurrentPage: true },
  ],
  '/settings/non-conformity/action-status': [
    { label: 'Non-Conformity', isCurrentPage: true },
    { label: 'Action Status', isCurrentPage: true },
  ],
  '/settings/information-asset/asset-type': [
    { label: 'Information Asset', href: '/information-asset' },
    { label: 'Asset Type', isCurrentPage: true },
  ],
  '/settings/organisation-preference': [{ label: 'Organisation Preference', isCurrentPage: true }],
  '/settings/pci-scope/people/access-type': [
    { label: 'PCI Scoping Configuration', isCurrentPage: true },
    { label: 'Access Type', isCurrentPage: true },
  ],
  '/settings/pci-scope/people/training-type': [
    { label: 'PCI Scoping Configuration', isCurrentPage: true },
    { label: 'Training Type', isCurrentPage: true },
  ],
  '/settings/pci-scope/process/data-handling-type': [
    { label: 'PCI Scoping Configuration', isCurrentPage: true },
    { label: 'Data Handling Type', isCurrentPage: true },
  ],
  '/settings/pci-scope/process/frequency-type': [
    { label: 'PCI Scoping Configuration', isCurrentPage: true },
    { label: 'Frequency Type', isCurrentPage: true },
  ],
  '/settings/classification-label': [
    { label: 'Settings', href: '/settings' },
    { label: 'Classification Label', isCurrentPage: true },
  ],
  '/ai-register': [
    { label: 'Dashboard', href: '/ai-register/dashboard' },
    {
      label: 'AI Register',
      isCurrentPage: true,
    },
  ],
  '/ai-register/dashboard': [
    { label: 'AI Register', href: '/ai-register' },
    { label: 'Dashboard', isCurrentPage: true },
  ],
  '/settings/ai-register/type': [
    { label: 'AI Register', href: '/ai-register' },
    { label: 'AI Register Type', isCurrentPage: true },
  ],
  '/settings/ai-register/permitted-data': [
    { label: 'AI Register', href: '/ai-register' },
    { label: 'Permitted Data', isCurrentPage: true },
  ],
  '/settings/ai-register/screening-questionnaire': [
    { label: 'AI Register', href: '/ai-register' },
    { label: 'AI Screening Questionnaire', isCurrentPage: true },
  ],
  '/settings/information-security-objectives/status': [
    { label: 'Settings', href: '/settings' },
    { label: 'Objective Status', isCurrentPage: true },
  ],
  '/settings/information-security-objectives/priority': [
    { label: 'Settings', href: '/settings' },
    { label: 'Objective Priority', isCurrentPage: true },
  ],
  '/settings/information-security-objectives/reporting-format': [
    { label: 'Settings', href: '/settings' },
    { label: 'Objective Reporting Format', isCurrentPage: true },
  ],
  '/settings/information-security-objectives/analysis-evaluation-frequency': [
    { label: 'Settings', href: '/settings' },
    { label: 'Objective Analysis & Evaluation Frequency', isCurrentPage: true },
  ],
  '/settings/information-security-objectives/monitoring-frequency': [
    { label: 'Settings', href: '/settings' },
    { label: 'Objective Monitoring Frequency', isCurrentPage: true },
  ],
  '/settings/information-security-objectives/measure-type': [
    { label: 'Settings', href: '/settings' },
    { label: 'Objective Measure Type', isCurrentPage: true },
  ],
  '/settings/information-security-objectives/area': [
    { label: 'Settings', href: '/settings' },
    { label: 'Objective Area', isCurrentPage: true },
  ],
  '/information-security-objectives': [
    { label: 'Governance', href: '#' },
    { label: 'Information Security Objectives', isCurrentPage: true },
  ],
  '/isms-assurance-calendar': [
    { label: 'Governance', href: '#' },
    { label: 'ISMS Assurance Calendar', isCurrentPage: true },
  ],
  '/add-information-security-objective': [
    { label: 'Information Security Objectives', href: '/information-security-objectives' },
    { label: 'Add Objective', isCurrentPage: true },
  ],
  '/add-information-security-objective/:id': [
    { label: 'Information Security Objectives', href: '/information-security-objectives' },
    { label: 'Edit Objective', isCurrentPage: true },
  ],
  '/information-asset': [
    { label: 'Asset Dashboard', href: '/information-asset-report' },
    { label: 'Asset Register', isCurrentPage: true },
  ],
  '/information-asset-report': [
    { label: 'Asset Register', href: '/information-asset' },
    { label: 'Asset Dashboard', isCurrentPage: true },
  ],
  '/run-zero-asset': [{ label: 'Run Zero Asset', isCurrentPage: true }],
  '/run-zero-asset-dashboard': [{ label: 'Run Zero Asset Dashboard', isCurrentPage: true }],
  '/manage-staff': [{ label: 'Manage Staff', isCurrentPage: true }],
  '/ai-chat': [{ label: 'CISO Adapt AI Chat', isCurrentPage: true }],
  '/manage-department': [
    { label: 'Manage Staff', href: '/manage-staff' },
    { label: 'Manage Department', isCurrentPage: true },
  ],
  '/profile': [
    { label: 'Settings', href: '/settings' },
    { label: 'Profile', isCurrentPage: true },
  ],
  '/settings/profile': [
    { label: 'Settings', href: '/settings' },
    { label: 'Profile', isCurrentPage: true },
  ],
  '/settings/mfa': [
    { label: 'Settings', href: '/settings' },
    { label: 'MFA Setup', isCurrentPage: true },
  ],
  '/risk-dashboard': [{ label: 'Risk Dashboard', isCurrentPage: true }],
  '/risk-list': [
    { label: 'Risk Dashboard', href: '/risk-dashboard' },
    { label: 'Manage Organisation Risk', isCurrentPage: true },
  ],
  '/plan-of-action': [
    { label: 'Risk Dashboard', href: '/risk-dashboard' },
    { label: 'Manage Organisation Risk', href: '/risk-list' },
    { label: 'Plan Of Action', isCurrentPage: true },
  ],
  '/add-risk': [
    { label: 'Risk Dashboard', href: '/risk-dashboard' },
    { label: 'Manage Organisation Risk', href: '/risk-list' },
    { label: 'Add Risk', isCurrentPage: true },
  ],
  '/risk-tracking': [
    { label: 'Risk Dashboard', href: '/risk-dashboard' },
    { label: 'Manage Organisation Risk', href: '/risk-list' },
    { label: 'Risk Tracking', isCurrentPage: true },
  ],
  '/settings/risk/vulnerability': [
    { label: 'Risk Dashboard', href: '/risk-dashboard' },
    { label: 'Manage Organisation Risk', href: '/risk-list' },
    { label: 'Vulnerability', isCurrentPage: true },
  ],
  '/risk/vulnerability/dashboard': [{ label: 'Vulnerability Dashboard', isCurrentPage: true }],
  '/risk/vulnerability': [
    { label: 'Vulnerability Dashboard', href: '/risk/vulnerability/dashboard' },
    { label: 'Vulnerability', isCurrentPage: true },
  ],
  '/settings/risk/threat-category': [
    { label: 'Risk Dashboard', href: '/risk-dashboard' },
    { label: 'Manage Organisation Risk', href: '/risk-list' },
    { label: 'Threat Category', isCurrentPage: true },
  ],
  '/settings/risk/threat-source': [
    { label: 'Risk Dashboard', href: '/risk-dashboard' },
    { label: 'Manage Organisation Risk', href: '/risk-list' },
    { label: 'Threat Source', isCurrentPage: true },
  ],
  '/settings/risk/threat-actor': [
    { label: 'Risk Dashboard', href: '/risk-dashboard' },
    { label: 'Manage Organisation Risk', href: '/risk-list' },
    { label: 'Threat Actor', isCurrentPage: true },
  ],
  '/settings/risk/threat': [
    { label: 'Risk Dashboard', href: '/risk-dashboard' },
    { label: 'Manage Organisation Risk', href: '/risk-list' },
    { label: 'Threat', isCurrentPage: true },
  ],
  '/assessments': [
    { label: 'Compliance Dashboard', href: '/compliance-report' },
    { label: 'Assessments', isCurrentPage: true },
  ],
  '/update-password': [
    { label: 'Settings', href: '/settings' },
    { label: 'Update Password', isCurrentPage: true },
  ],
  '/incident-dashboard': [{ label: 'Incident Dashboard', isCurrentPage: true }],
  '/incident': [
    { label: 'Incident Dashboard', href: '/incident-dashboard' },
    { label: 'Incident Register', isCurrentPage: true },
  ],
  '/add-incident': [
    { label: 'Incident Dashboard', href: '/incident-dashboard' },
    { label: 'Incident List', href: '/incident' },
    { label: 'Add Incident', isCurrentPage: true },
  ],
  '/settings/risk/incident-types': [
    { label: 'Incident Dashboard', href: '/incident-dashboard' },
    { label: 'Incident Register', href: '/incident' },
    { label: 'Incident Types', isCurrentPage: true },
  ],
  '/settings/roles-and-permissions': [{ label: 'Roles & Permissions', isCurrentPage: true }],
  '/settings/assessment-tooltip': [{ label: 'Assessment Tooltip', isCurrentPage: true }],
  '/service-provider-dashboard': [{ label: 'Supply Chain Management Dashboard', isCurrentPage: true }],
  '/service-provider-list': [
    { label: 'Supply Chain Management Dashboard', href: '/service-provider-dashboard' },
    { label: 'Supply Chain Register', isCurrentPage: true },
  ],
  '/add-service-provider': [
    { label: 'Supply Chain Register', href: '/service-provider-list' },
    { label: 'Add Supplier', isCurrentPage: true },
  ],
  '/assessment-library': [
    { label: 'Supply Chain Register', href: '/service-provider-list' },
    { label: 'Questionnaire Library', isCurrentPage: true },
  ],
  '/archive-assessment': [
    { label: 'Compliance Dashboard', href: '/compliance-report' },
    { label: 'Archive Assessment', isCurrentPage: true },
  ],
  '/service-provider-assessment': [
    { label: 'Supply Chain Register', href: '/service-provider-list' },
    { label: 'Supply Chain Assessment', isCurrentPage: true },
  ],
  '/add-service-provider-assessment': [
    { label: 'Supply Chain Assessment', href: '/service-provider-assessment' },
    { label: 'Supply Chain Assessment', isCurrentPage: true },
  ],
  '/service-provider-incoming-questionnaire': [
    { label: 'Supply Chain Register', href: '/service-provider-list' },
    { label: 'Incoming Questionnaire', isCurrentPage: true },
  ],
  '/add-assessment-library': [
    { label: 'Questionnaire Library', href: '/assessment-library' },
    { label: 'Add Questionnaire Library', isCurrentPage: true },
  ],

  // Country Regulatory Register
  '/country-regulatory-register-oos': [{ label: 'Country Regulatory Register ', isCurrentPage: true }],
  '/country-regulatory-register-oos/jurisdiction': [
    { label: 'Country Regulatory Register ', href: '/country-regulatory-register-oos' },
    { label: 'Jurisdiction', isCurrentPage: true },
  ],
  '/country-regulatory-register-oos/operation-type': [
    { label: 'Country Regulatory Register ', href: '/country-regulatory-register-oos' },
    { label: 'Operation Type', isCurrentPage: true },
  ],
  '/country-regulatory-register-oos/regulatory-category': [
    { label: 'Country Regulatory Register ', href: '/country-regulatory-register-oos' },
    { label: 'Regulatory Category', isCurrentPage: true },
  ],
  '/country-regulatory-register-oos/region': [
    { label: 'Country Regulatory Register ', href: '/country-regulatory-register-oos' },
    { label: 'Region', isCurrentPage: true },
  ],
  '/country-regulatory-register-oos/risk-type': [
    { label: 'Country Regulatory Register ', href: '/country-regulatory-register-oos' },
    { label: 'Risk Type', isCurrentPage: true },
  ],
  '/exemptions-register': [{ label: 'Exemptions Register', isCurrentPage: true }],
  '/organisational-context': [{ label: 'Organisational Context', isCurrentPage: true }],
  '/change-management-process': [{ label: 'Change Management Process', isCurrentPage: true }],
  '/settings/change-management-process-category': [
    { label: 'Change Management Process', href: '/change-management-process' },
    { label: 'Change Management Process Category', isCurrentPage: true },
  ],
  '/release-notes': [{ label: 'Release Notes', isCurrentPage: true }],
  '/bug-report': [{ label: 'Bug Report & Feature Request', isCurrentPage: true }],

  '/risk-assessment/setup': [
    { label: 'System Risk Assessment', href: '#' },
    { label: 'Setup', isCurrentPage: true },
  ],
  '/risk-assessment/help': [
    { label: 'System Risk Assessment', href: '#' },
    { label: 'Help', isCurrentPage: true },
  ],
  '/risk-assessment/assessment': [
    { label: 'System Risk Assessment', href: '#' },
    { label: 'Assessment', isCurrentPage: true },
  ],
  '/risk-assessment/report': [
    { label: 'System Risk Assessment', href: '#' },
    { label: 'Report', isCurrentPage: true },
  ],
  '/risk-assessment': [{ label: 'System Risk Assessment', isCurrentPage: true }],
  '/people-risk-assessment': [{ label: 'People Risk Assessment', isCurrentPage: true }],
  '/organisation/ai-token-usage': [{ label: 'AI Token Usage', isCurrentPage: true }],
  '/email-template': [{ label: 'Email Template Customisation', isCurrentPage: true }],
  '/user-manual': [{ label: 'User Manual', isCurrentPage: true }],
  '/document-repo': [{ label: 'Document Repository', isCurrentPage: true }],
  '/organisation/resource': [{ label: 'FAQ', isCurrentPage: true }],
  '/organisation/ai-model': [{ label: 'AI Models', isCurrentPage: true }],
  '/organisation/sharepoint-config': [
    { label: 'API Integrations', href: '/settings/external-integration' },
    { label: 'SharePoint Config', isCurrentPage: true },
  ],
  '/ctra/programs': [
    { label: 'Cyber Threat Risk Assessment', isCurrentPage: true },
    { label: 'Programs', isCurrentPage: true },
  ],
  '/ctra/zones': [
    { label: 'Cyber Threat Risk Assessment', isCurrentPage: true },
    { label: 'Asset Zones', isCurrentPage: true },
  ],
  '/ctra/engagements': [
    { label: 'Cyber Threat Risk Assessment', isCurrentPage: true },
    { label: 'Engagements', isCurrentPage: true },
  ],
  '/ctra/engagements/add': [
    { label: 'Cyber Threat Risk Assessment', isCurrentPage: true },
    { label: 'Engagements', isCurrentPage: true },
    { label: 'Add Engagement', isCurrentPage: true },
  ],

  '/ctra/manage-soa': [
    { label: 'Cyber Threat Risk Assessment', isCurrentPage: true },
    { label: 'Manage SoA', isCurrentPage: true },
  ],
  '/ctra/threat-scenario': [
    { label: 'Cyber Threat Risk Assessment', isCurrentPage: true },
    { label: 'Threat Scenario', isCurrentPage: true },
  ],
  '/organisation/ai/standard-qa': [
    { label: 'AI', isCurrentPage: true },
    { label: 'Standard QA', isCurrentPage: true },
  ],
  '/organisation/ai-changes': [{ label: 'AI Changes', isCurrentPage: true }],
  // ========================================
  // SUPER ADMIN ROUTES
  // ========================================
  '/admin/organisations': [{ label: 'Organisations', isCurrentPage: true }],
  '/admin/dashboard-defaults': [{ label: 'Dashboard Chart Types', isCurrentPage: true }],
  '/admin/organisations/child-organisations/:id': [
    { label: 'Organisations', href: '/admin/organisations' },
    { label: 'Child Organisations', isCurrentPage: true },
  ],
  '/admin/organisation-users': [{ label: 'Organisation Management', isCurrentPage: true }],
  '/admin/clerk-users': [{ label: 'Clerk Users', isCurrentPage: true }],
  '/admin/assessment': [{ label: 'Assessments', isCurrentPage: true }],
  '/admin/admins': [{ label: 'Admin Management', isCurrentPage: true }],
  '/admin/fcm-tokens': [{ label: 'Manage FCM Tokens', isCurrentPage: true }],
  '/admin/standards': [{ label: 'Standards', isCurrentPage: true }],
  '/admin/standard-levels': [{ label: 'Standard Levels', isCurrentPage: true }],
  '/admin/saq-types': [{ label: 'SAQ Types', isCurrentPage: true }],
  '/admin/fiscal-years': [{ label: 'Fiscal Years', isCurrentPage: true }],
  '/admin/controls': [{ label: 'Controls', isCurrentPage: true }],
  '/admin/control-groups': [{ label: 'Control Groups', isCurrentPage: true }],
  '/admin/control-mapping': [{ label: 'Control Mapping', isCurrentPage: true }],
  '/admin/control-questionnaires': [{ label: 'Control Questionnaires', isCurrentPage: true }],
  '/admin/maturity-models': [{ label: 'Maturity Models', isCurrentPage: true }],
  '/admin/maturity-levels': [{ label: 'Maturity Levels', isCurrentPage: true }],
  '/admin/security-question-groups': [{ label: 'Security Question Groups', isCurrentPage: true }],
  '/admin/security-questionnaires': [{ label: 'Security Questionnaires', isCurrentPage: true }],
  '/admin/resources': [{ label: 'Manage FAQ', isCurrentPage: true }],
  '/admin/user-manual': [{ label: 'Manage User Manual', isCurrentPage: true }],
  '/admin/mcp-server-extension': [{ label: 'Manage MCP Server Extension', isCurrentPage: true }],
  '/admin/helpful-tips': [{ label: 'Manage Helpful Tips', isCurrentPage: true }],
  '/admin/module': [{ label: 'Module', isCurrentPage: true }],
  '/admin/pci-dss-task': [{ label: 'PCI DSS v4.0.1 Tasks', isCurrentPage: true }],
  '/admin/pci-dss-evidence-pack-task': [{ label: 'PCI (Evidence Pack) Tasks', isCurrentPage: true }],
  '/admin/it-privileged-operations': [{ label: 'IT Privileged Operations', isCurrentPage: true }],
  '/admin/release-notes': [{ label: 'Release Notes', isCurrentPage: true }],
  '/admin/file-sensitivity': [{ label: 'Classification Label', isCurrentPage: true }],
  '/admin/phished-recipient-tag-color': [{ label: 'Phished Recipient Tag Color', isCurrentPage: true }],
  '/admin/bug-report': [{ label: 'Manage Bug Report', isCurrentPage: true }],
  '/admin/signup-request': [{ label: 'Manage Signup Requests', isCurrentPage: true }],
  '/admin/signup-request/report': [
    { label: 'Manage Signup Requests', href: '/admin/signup-request' },
    { label: 'Signup Requests Report', isCurrentPage: true },
  ],
  '/admin/subscriptions': [{ label: 'Organisation Subscription', isCurrentPage: true }],
  '/admin/subscription-type': [{ label: 'Manage Subscription Type', isCurrentPage: true }],
  '/admin/risk-assessment/system-exposure': [
    { label: 'System Risk Assessment Setup', href: '#' },
    { label: 'System Exposure', isCurrentPage: true },
  ],
  '/admin/risk-assessment/hosting-model': [
    { label: 'System Risk Assessment Setup', href: '#' },
    { label: 'Hosting Model', isCurrentPage: true },
  ],
  '/admin/risk-assessment/data-classification': [
    { label: 'System Risk Assessment Setup', href: '#' },
    { label: 'Data Classification', isCurrentPage: true },
  ],
  '/admin/risk-assessment/user-base': [
    { label: 'System Risk Assessment Setup', href: '#' },
    { label: 'User Base', isCurrentPage: true },
  ],
  '/admin/risk-assessment/assessment-type': [
    { label: 'System Risk Assessment Setup', href: '#' },
    { label: 'Assessment Type', isCurrentPage: true },
  ],
  '/admin/risk-assessment/questionnaire-library': [
    { label: 'System Risk Assessment Setup', href: '#' },
    { label: 'Questionnaire Library', isCurrentPage: true },
  ],
  '/admin/people-risk-assessment/assessment-type': [
    { label: 'People Risk Assessment Setup', href: '#' },
    { label: 'Assessment Type', isCurrentPage: true },
  ],
  '/admin/people-risk-assessment/questionnaire-library': [
    { label: 'People Risk Assessment Setup', href: '#' },
    { label: 'Questionnaire Library', isCurrentPage: true },
  ],
  '/admin/ai-models': [{ label: 'AI Models', isCurrentPage: true }],
  '/admin/ai-prompt-catalog': [{ label: 'AI Prompt Catalog', isCurrentPage: true }],
  '/admin/ai-token-usage': [{ label: 'AI Token Usage', isCurrentPage: true }],
  '/admin/server-logs': [{ label: 'Server Logs', isCurrentPage: true }],
  '/admin/activity-logs': [{ label: 'Manage Activity Logs', isCurrentPage: true }],
  '/admin/email-logs': [{ label: 'Email Logs', isCurrentPage: true }],
  '/admin/activity-analytics': [{ label: 'Activity Analytics', isCurrentPage: true }],
  '/admin/email-template': [{ label: 'Email Template Customisation', isCurrentPage: true }],
  '/admin/common-pdf-template': [{ label: 'PDF Template', isCurrentPage: true }],
  '/admin/assistant-chat-history': [{ label: 'Assistant Chat History', isCurrentPage: true }],
  '/admin/scheduler-config': [{ label: 'Scheduler Configuration', isCurrentPage: true }],
  '/admin/ai-database': [{ label: 'AI Database', isCurrentPage: true }],
  '/admin/database': [{ label: 'Database', isCurrentPage: true }],
  '/admin/api-docs-tokens': [{ label: 'API Docs Tokens', isCurrentPage: true }],
  '/admin/ai-rag/control-embeddings': [{ label: 'Control Embeddings', isCurrentPage: true }],
  '/admin/ai-rag/evidence-index': [{ label: 'Evidence Index', isCurrentPage: true }],
  '/admin/ai-rag/activity-log': [{ label: 'AI Activity Log', isCurrentPage: true }],
  '/admin/ai-rag/token-usage': [{ label: 'AI Service Token Usage', isCurrentPage: true }],
  '/admin/ai-rag/policy-templates': [{ label: 'Policy Templates', isCurrentPage: true }],
  '/admin/ai-rag/policy-packs': [{ label: 'Policy Packs', isCurrentPage: true }],
  '/admin/ai-rag/service-status': [{ label: 'AI Service Status', isCurrentPage: true }],
  '/admin/user-guides': [{ label: 'User Guides', isCurrentPage: true }],
  '/admin/user-guide-api-keys': [{ label: 'User Guide API Keys', isCurrentPage: true }],
  '/admin/ai-rag/agents': [{ label: 'AI RAG Agents', isCurrentPage: true }],
  // ========================================
  // VENDOR ROUTES
  // ========================================
  '/vendor/assessments': [{ label: 'Vendor Assessments', isCurrentPage: true }],
  '/admin/vendor-baseline': [{ label: 'Vendor Baseline', isCurrentPage: true }],
}
